/**
 * The calendar surface (`040-api-contract.md` → "Events & calendar"). **Reads only.**
 *
 * Same rule as `pages.ts` and `tags.ts`: there are no write functions in this file and
 * there must not be. Every event and category mutation is an outbox entry
 * (`features/calendar/useEventMutations.ts`) — a `postEvent()` helper here would be the one
 * call site that forgets the queue (`112-offline-drafts.md` §5).
 *
 * One range query serves all three views. `GET /api/calendar` returns events **and** page
 * due dates merged, "because every calendar view needs both and two round-trips for one
 * screen is silly" (`040`). There is deliberately no separate `/api/dues` to call, and
 * rendering a range must never issue two requests (`140-roadmap.md` §M5, criterion 4).
 */
import { apiFetch } from '@/api/client';

/* ── DTOs ───────────────────────────────────────────────────────────────────── */

export interface EventCategory {
  id: string;
  name: string;
  /** REQUIRED `#rrggbb` (`080-calendar.md` §6). A colourless category is a contradiction. */
  color: string;
}

export interface EventCategoriesResponse {
  categories: EventCategory[];
}

/** A page an event is linked to (`event_page`, `080` §8). Enough to render a chip and a link. */
export interface EventPageRef {
  id: string;
  title: string;
  icon?: string | null;
}

export type EventStatus = 'open' | 'done';

/**
 * `endsAt` is **always exclusive**, in both modes — one rule, no special cases (`080` §1.1).
 * `null` means "a point in time" (timed) or "a single day" (all-day). Do not "fix" a null
 * end into `startsAt` and do not store `23:59:59`; `080` §1.2 explains at length why both
 * are wrong.
 */
export interface CalendarEvent {
  id: string;
  title: string;
  description?: string | null;
  allDay: boolean;
  startsAt: string;
  endsAt: string | null;
  status: EventStatus;
  completedAt?: string | null;
  category: EventCategory | null;
  pages: EventPageRef[];
}

/**
 * A page with a deadline. **It is not an event** (`080` §7): no category, no duration, no
 * done state. The API keeps it in its own array precisely so the conceptual distinction
 * cannot rot — do not merge these into `events` anywhere.
 */
export interface PageDue {
  pageId: string;
  title: string;
  icon: string | null;
  dueAt: string;
  dueAllDay: boolean;
}

export interface CalendarResponse {
  events: CalendarEvent[];
  dues: PageDue[];
}

/** `GET /api/pages/{id}/events` (`080` §8, "Page → events"). */
export interface PageEventsResponse {
  events: CalendarEvent[];
}

/* ── Reads ──────────────────────────────────────────────────────────────────── */

/**
 * `from` inclusive, `to` exclusive, both `YYYY-MM-DD` **in the user's timezone** — the
 * server does the range → UTC-instant conversion, because that is where the DST bugs live
 * and there must be exactly one place in the codebase that computes it (`080` §1.5).
 */
export function getCalendar(
  from: string,
  to: string,
  includeDue = true,
): Promise<CalendarResponse> {
  const params = new URLSearchParams({ from, to, includeDue: String(includeDue) });
  return apiFetch<CalendarResponse>(`/api/calendar?${params.toString()}`);
}

export function getEventCategories(): Promise<EventCategoriesResponse> {
  return apiFetch<EventCategoriesResponse>('/api/event-categories');
}

/**
 * The second half of the wiki-graph payoff (`080` §8): the NPC page shows both which pages
 * mention him (backlinks) and which sessions he is scheduled to appear in.
 *
 * **Interpretation, flagged rather than assumed:** `040-api-contract.md` specifies the
 * event → pages direction (`pageIds` on the event) but names no endpoint for the reverse,
 * while `080` §8 requires the reverse surface and says it is "backed by the
 * `(page_id, event_id)` index on `event_page`". The endpoint below mirrors
 * `GET /api/pages/{id}/backlinks`, which is the only shape in the contract that answers the
 * same kind of question. If the backend named it otherwise, this one line moves; the panel
 * degrades to hidden (never to an error) while it 404s.
 */
export function getPageEvents(id: string): Promise<PageEventsResponse> {
  return apiFetch<PageEventsResponse>(`/api/pages/${id}/events`);
}

/* ── Write bodies (the shapes the outbox carries) ───────────────────────────── */

export interface CreateEventBody {
  /**
   * Client-generated ULID, exactly as for a page and a tag. It is what lets a create and
   * the writes that reference it be queued together while offline, and what makes the event
   * appear in the view before the `POST` has left the browser (`080` §9, "Save").
   */
  id: string;
  title: string;
  description?: string | null;
  allDay: boolean;
  startsAt: string;
  endsAt?: string | null;
  categoryId?: string | null;
  /** A FULL REPLACEMENT of the event↔page links (`040`). There is no add/remove-one. */
  pageIds?: string[];
}

export type UpdateEventBody = Partial<Omit<CreateEventBody, 'id'>>;
